import { DateTime } from 'luxon';
import { Weather } from '../../hooks/weather/weather';
import { TimeSeries, WeatherForecast } from '../../hooks/weather/smhi';

export const ddWeatherForecast: Weather[] = [
  {
    symbol: 'Clear sky',
    temperature: 23,
    time: DateTime.now(),
    windDirection: 180,
    windSpeed: 1,
  },
  {
    symbol: 'Halfclear sky',
    temperature: 14,
    time: DateTime.now(),
    windDirection: 234,
    windSpeed: 5,
  },
  {
    symbol: 'Overcast',
    temperature: 14,
    time: DateTime.now(),
    windDirection: 123,
    windSpeed: 3,
  },
  {
    symbol: 'Fog',
    temperature: 11,
    time: DateTime.now(),
    windDirection: 195,
    windSpeed: 1,
  },
  {
    symbol: 'Light rain showers',
    temperature: 6,
    time: DateTime.now(),
    windDirection: 76,
    windSpeed: 2,
  },
  {
    symbol: 'Moderate snowfall',
    temperature: -1,
    time: DateTime.now(),
    windDirection: 32,
    windSpeed: 4,
  },
  {
    symbol: 'Thunder',
    temperature: 17,
    time: DateTime.now(),
    windDirection: 293,
    windSpeed: 6,
  },
];

// Generates a SNOW1gv1-shaped forecast covering the next ~10 days, hourly for
// the first 24h and then 6-hourly. Mirrors the structure of a real response so
// MSW-backed gallery/test runs exercise the same parsing path as production.
const buildDummyTimeSeries = (start: DateTime): TimeSeries[] => {
  const series: TimeSeries[] = [];
  const symbolCycle = [1, 2, 3, 4, 6, 8, 18, 11, 25, 5];
  const totalHours = 240;

  for (let hour = 0; hour <= totalHours; hour++) {
    if (hour > 24 && hour % 6 !== 0) continue;
    const time = start.plus({ hours: hour });
    const dayPhase = ((time.hour - 6 + 24) % 24) / 24;
    const air_temperature =
      8 + Math.round(10 * Math.sin(dayPhase * Math.PI) * 10) / 10;
    const wind_speed = 2 + Math.round(((hour % 5) + 0.5) * 10) / 10;
    const wind_from_direction = (180 + hour * 7) % 360;
    const symbol_code = symbolCycle[hour % symbolCycle.length];

    series.push({
      time: time.toUTC().toISO({ suppressMilliseconds: true }) ?? '',
      intervalParametersStartTime:
        time
          .minus({ hours: hour === 0 ? 0 : 1 })
          .toUTC()
          .toISO({ suppressMilliseconds: true }) ?? '',
      data: {
        air_temperature,
        wind_from_direction,
        wind_speed,
        wind_speed_of_gust: wind_speed * 2,
        relative_humidity: 60,
        air_pressure_at_mean_sea_level: 1015,
        visibility_in_air: 30,
        thunderstorm_probability: symbol_code === 11 ? 60 : 0,
        probability_of_frozen_precipitation: symbol_code >= 12 ? 0.5 : 0,
        cloud_area_fraction: symbol_code >= 5 ? 6 : 1,
        low_type_cloud_area_fraction: 0,
        medium_type_cloud_area_fraction: 0,
        high_type_cloud_area_fraction: 0,
        cloud_base_altitude: 0,
        cloud_top_altitude: 0,
        precipitation_amount_mean_deterministic: 0,
        precipitation_amount_mean: 0,
        precipitation_amount_min: 0,
        precipitation_amount_max: 0,
        precipitation_amount_median: 0,
        probability_of_precipitation: 0,
        precipitation_frozen_part: 0,
        predominant_precipitation_type_at_surface: 0,
        symbol_code,
      },
    });
  }

  return series;
};

const start = DateTime.now().startOf('hour');

export const ddWeather: WeatherForecast = {
  createdTime: start.toUTC().toISO({ suppressMilliseconds: true }) ?? '',
  referenceTime: start.toUTC().toISO({ suppressMilliseconds: true }) ?? '',
  geometry: { type: 'Point', coordinates: [15.582938, 56.199568] },
  timeSeries: buildDummyTimeSeries(start),
};
